import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  MobileSearchInput,
  Results,
  StyledSearch as Search,
  SpinnerWrapper,
  AutocompleteContainer
} from "./styles";
import AutoComplete from "../AutoComplete";
import { SearchContext } from "../../context";
import { titleCase } from "../../utils/helpers";
import { Input } from "antd";
import Icon from "../Icon";
import { useTheme } from "styled-components";
import { CustomSpinner } from "../SpinnerSmall/styles";
import { getSearchIcon, filterByVisibility } from "./utils";
import { ENTITY_BY_FILTER_NAME } from '../../constants/entityTypes'
import { SEARCH_CHIP_CONFIG } from './constants'

const Searchbar = ({ mobile, toggleSearchBar }) => {
  const {
    suggestions,
    fetchSearchResult,
    searchValue: value,
    setSearchValue: setValue,
    getSuggestions,
    tagSearchResults,
    instrumentSearchResults,
    genreSearchResults,
    setIsSearching,
    aggregateResults,
    setSelectedTrackList,
    isLoadingAutocomplete,
    setLoadingAutocomplete,
    showAllFiltersByDefault,
    queryAlbumDetail,
    queryPlaylistDetail,
    setPendingWidgetEntity
  } = useContext(SearchContext);

  const allSuggestions = useMemo(() => [
    ...suggestions,
    ...filterByVisibility(tagSearchResults, showAllFiltersByDefault),
    ...filterByVisibility(genreSearchResults, showAllFiltersByDefault),
    ...instrumentSearchResults,
    ...aggregateResults
  ], [suggestions, tagSearchResults, genreSearchResults, instrumentSearchResults, aggregateResults, showAllFiltersByDefault]);

  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [textSearching, setTextSearching] = useState(true);
  const searchRef = useRef(null);
  const theme = useTheme();

  // Dynamic search icon based on input and suggestions
  const searchIcon = useMemo(() => getSearchIcon(value, allSuggestions), [allSuggestions, value]);

  // Auto-highlight when there's an exact title match or only one suggestion
  useEffect(() => {
    if (!showSuggestions || !value) return;

    const exactMatchIndex = allSuggestions.findIndex(
      item => (item.name || item.title || "").toLowerCase() === value.toLowerCase()
    );

    if (exactMatchIndex !== -1) {
      setActiveSuggestion(exactMatchIndex + 1);
      setTextSearching(false);
      return;
    }

    if (allSuggestions.length === 1) {
      setActiveSuggestion(1);
      setTextSearching(false);
      return;
    }

    setActiveSuggestion(0);
    setTextSearching(true);
  }, [allSuggestions, showSuggestions, value]);

  const handleChange = async e => {
    if (e.target.value.length === 0) {
      setShowSuggestions(false);
    }

    const newValues = titleCase(e.target.value);
    setLoadingAutocomplete(true);
    setTextSearching(true);
    setActiveSuggestion(0);
    setValue(newValues);
    setShowSuggestions(true);

    await getSuggestions(newValues);
  };

  const handleKeyDown = useCallback(
    (e, autoCompleteKeyDown) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        setLoadingAutocomplete(false);
        e.target.blur();
        return;
      }
      else if (autoCompleteKeyDown) {
        autoCompleteKeyDown(e);
      }
    }, [setShowSuggestions, setActiveSuggestion, setLoadingAutocomplete]
  );

  // create a mutable ref to handler that gets refreshed with up-to-date redux store every rerender
  // this is necessary because of useEffect dependencies & redux
  // if you add songTime to useEffect dependencies the event listeners get added & removed way too often
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    const keyDownHandler = e => handleKeyDownRef.current(e);
    document.addEventListener("keydown", keyDownHandler);
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, []);

  // Search on selectable (item selected from dropdown)
  const runFilterSearch = (item) => {
    setValue("");
    setIsSearching(false);
    if (["track", "session", "playlist"].includes(item.type)) {
      redirect(item)
    } else {
      setSelectedTrackList(null);
      setPendingWidgetEntity(null);
      fetchSearchResult(item.name, item.type, item);
    }
  };

  const exactTitleMatches = suggestions.filter(x => {
    return x.title && x.title.toLowerCase() === value.toLowerCase();
  });

  // Search on text string
  const runTextSearch = (item) => {
    if (exactTitleMatches.length === 1) {
      // If we have typed the exact title of an album/track/playlist, 
      // we redirect to that album/track/playlist
      const match = exactTitleMatches[0];
      setIsSearching(false);
      redirect(match)
    } else {
      // Otherwise we run our ES text search 
      fetchSearchResult(item);
      setLoadingAutocomplete(false);
      setValue("");
    }
  };

  const onEnter = async item => {
    setShowSuggestions(false);
    if (value.length > 0) {
      if (typeof item !== "string") {
        runFilterSearch(item)
      } else {
        runTextSearch(item);
      }
    }
  };

  const redirect = (item) => {

    if (item.__typename === "Track") {
      setSelectedTrackList({ name: item.title || item.name, type: "Track", entity: item });
    } else if (item.__typename === "Album") {
      setPendingWidgetEntity({
        __typename: "Album",
        id: item.id,
        name: item.name,
        description: item.description,
        thumbnail: item.thumbnail,
        created_at: item.created_at
      });
      queryAlbumDetail(item);
    } else if (item.__typename === 'Playlist') {
      setPendingWidgetEntity({
        __typename: "Playlist",
        id: item.id,
        name: item.name,
        description: item.description,
        thumbnail: item.thumbnail
      });
      queryPlaylistDetail(item);
    }
    setValue("");
  };

  const renderOption = ({ iconName, item, type, index }) => {
    return (
      <div
        onClick={() => onEnter(item, type)}
        className={`result ${index === activeSuggestion ? "focused-result" : ""}`}
        key={index}
      >
        <Icon
          type={iconName}
          size="sm"
          color={theme.textLight}
          className="result-icon"
          hoverBackground={false}
        />
        <p className="name">{item.name}</p>
        <p className="type">{type}</p>
      </div>
    );
  };

  const renderAutocompleteOptions = suggestionsAll => {
    let count = 0;

    if (isLoadingAutocomplete && value.length > 0) {
      return (
        <AutocompleteContainer>
          <Results className="RESULTS CONTAINER">
            <SpinnerWrapper>
              <CustomSpinner />
            </SpinnerWrapper>
          </Results>
        </AutocompleteContainer>
      );
    }

    return (
      <AutocompleteContainer>
        <Results>
          {suggestionsAll.map((item) => {
            const config = ENTITY_BY_FILTER_NAME[item.type] ?? SEARCH_CHIP_CONFIG[item.type]
            if (!config) {
              return null
            }
            count++
            const { iconName, displayName } = config
            return renderOption({ iconName, item, type: displayName, index: count })
          })}
        </Results>
      </AutocompleteContainer>
    );
  };

  const shouldShowAutocomplete = suggestionsAll => ((isLoadingAutocomplete && value.length > 0) ||
    (showSuggestions && suggestionsAll.length > 0 && value.length > 0));


  return (
    <Container ref={searchRef}>
      <AutoComplete
        value={value}
        suggestions={allSuggestions}
        onEnter={onEnter}
        activeSuggestion={activeSuggestion}
        setActiveSuggestion={setActiveSuggestion}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        textSearching={textSearching}
        setTextSearching={setTextSearching}
      >
        {(onKeyDown, suggestionsAll) => {
          const isFocused = showSuggestions || value.length > 0;
          /** Desktop view */
          if (!mobile) {
            return (
              <div className="search">
                <Search $isFocused={isFocused}>
                  <Icon
                    type={searchIcon}
                    size="sm"
                    inheritColor
                    className="search-icon"
                    hoverBackground={false}
                  />
                  <input
                    className="input"
                    value={value}
                    onChange={handleChange}
                    placeholder="Search by title, keyword, etc."
                    onKeyDown={(e) => handleKeyDown(e, onKeyDown)}
                  />
                </Search>
                {shouldShowAutocomplete(suggestionsAll) && (
                  renderAutocompleteOptions(suggestionsAll)
                )}
              </div>
            );
          }
          return (
            /** Mobile view */
            <div>
              <MobileSearchInput>
                <img
                  className="search-icon"
                  src={require("../../assets/search.svg")}
                  alt="search"
                />
                <Input
                  placeholder="Search"
                  autoFocus
                  className="focus-visible"
                  value={value}
                  onChange={handleChange}
                  onKeyDown={(e) => handleKeyDown(e, onKeyDown)}
                />
                <img
                  alt="close-btn"
                  src={require("../../assets/close.svg")}
                  className="close-icon"
                  onClick={() => toggleSearchBar(false)}
                />
              </MobileSearchInput>
              {shouldShowAutocomplete(suggestionsAll) && (
                renderAutocompleteOptions(suggestionsAll)
              )}
            </div>
          );
        }}
      </AutoComplete>
    </Container>
  );
};

export default Searchbar;
